import { useEffect, useMemo, useState } from "react";
import { api, fetchIncident } from "./api";
import { getOrCreateClientId } from "./pushClient";
import { compass } from "./proximity";
import { matchPerimeter } from "./dedupe";
import { useSavedLocations } from "./SavedLocationsContext";

const SEEN_KEY = "fw_follow_seen";
const readSeen = () => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY)) || {};
  } catch {
    return {};
  }
};

/** Remember that the user opened this followed fire just now. */
export function markFollowSeen(incidentId) {
  const s = readSeen();
  s[incidentId] = new Date().toISOString();
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(s));
  } catch {
    /* storage full/blocked — badge just stays */
  }
}

/** Has a followed fire seen activity since the user last opened it?
 *  Never-opened fires only flag activity from the last 24h. */
export function hasFollowUpdate(incidentId, activityIso) {
  if (!activityIso) return false;
  const act = new Date(activityIso).getTime();
  if (Number.isNaN(act)) return false;
  const seen = readSeen()[incidentId];
  if (!seen) return Date.now() - act < 24 * 36e5;
  return act > new Date(seen).getTime();
}

export function distMi(lat1, lon1, lat2, lon2) {
  const p = Math.PI / 180;
  const a =
    Math.sin(((lat2 - lat1) * p) / 2) ** 2 +
    Math.cos(lat1 * p) * Math.cos(lat2 * p) * Math.sin(((lon2 - lon1) * p) / 2) ** 2;
  return 2 * 3958.8 * Math.asin(Math.sqrt(a));
}

export function bearingDeg(lat1, lon1, lat2, lon2) {
  const mid = (((lat1 + lat2) / 2) * Math.PI) / 180;
  const dx = (lon2 - lon1) * Math.cos(mid);
  const dy = lat2 - lat1;
  return ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
}

function perimeterVertices(geom) {
  if (!geom) return [];
  const polys =
    geom.type === "MultiPolygon" ? geom.coordinates
    : geom.type === "Polygon" ? [geom.coordinates]
    : [];
  const pts = [];
  for (const poly of polys) {
    for (const ring of poly) {
      const step = Math.max(1, Math.floor(ring.length / 400));
      for (let i = 0; i < ring.length; i += step) pts.push(ring[i]);
    }
  }
  return pts;
}

/** Add `edge` (nearest perimeter point + distance from the saved location)
 *  to followed fires that have a mapped perimeter. */
export function enrichEdgeDistances(fires, perimeters) {
  if (!fires?.length || !perimeters?.length) return fires || [];
  return fires.map((f) => {
    if (!f.ref) return f;
    const p = matchPerimeter(f, perimeters);
    if (!p?.geometry) return f;
    let best = null;
    for (const [lon, lat] of perimeterVertices(p.geometry)) {
      const d = distMi(f.ref.ref_lat, f.ref.ref_lon, lat, lon);
      if (!best || d < best.distance_mi) best = { lat, lon, distance_mi: d };
    }
    if (!best) return f;
    best.compass = compass(bearingDeg(f.ref.ref_lat, f.ref.ref_lon, best.lat, best.lon));
    best.mapped_at = p.modified_at || p.updated_at || null;
    return { ...f, edge: best };
  });
}

export function useFollowedFires() {
  const { locations } = useSavedLocations();
  const [fires, setFires] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cid = getOrCreateClientId();
        const r = await api.get(`/push/follows/${cid}`);
        const ids = r.data.incident_ids || [];
        if (!ids.length) return;
        const details = await Promise.all(
          ids.map((id) => fetchIncident(id).catch(() => null)),
        );
        if (alive) setFires(details.filter((d) => d && d.latitude != null));
      } catch {
        /* panel simply stays hidden */
      }
    })();
    return () => { alive = false; };
  }, []);

  return useMemo(() => {
    // Distance lines anchor ONLY to saved location pins — device geolocation
    // proved unreliable (IP-based positions hundreds of miles off).
    const refs = (locations || []).map((l) => ({ lat: l.lat, lon: l.lon, label: l.label }));
    const enriched = fires.map((f) => {
      let best = null;
      for (const r of refs) {
        const d = distMi(r.lat, r.lon, f.latitude, f.longitude);
        if (!best || d < best.distance_mi) {
          best = {
            distance_mi: d,
            compass: compass(bearingDeg(r.lat, r.lon, f.latitude, f.longitude)),
            ref_label: r.label,
            ref_lat: r.lat,
            ref_lon: r.lon,
          };
        }
      }
      return { ...f, ref: best };
    });
    const unfollow = async (incidentId) => {
      await api.post("/push/unfollow", {
        client_id: getOrCreateClientId(),
        incident_id: incidentId,
      });
      setFires((prev) => prev.filter((f) => f.id !== incidentId));
    };
    const removeLocal = (incidentId) => {
      setFires((prev) => prev.filter((f) => f.id !== incidentId));
    };
    return { fires: enriched, unfollow, removeLocal };
  }, [fires, locations]);
}
